import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router"
import { motion } from "motion/react"
import { Eye, EyeOff } from "lucide-react"
import { CheckCircle, Mail01 } from "@untitledui/icons"
import { toast } from "sonner"

import { authApi } from "@/lib/api/auth"
import { api, ApiError } from "@/lib/api/client"
import { useAuth } from "@/lib/auth/auth-context"
import { WelcomeShell, WelcomeCard } from "@/components/welcome/welcome-shell"
import {
  Field,
  PrimaryButton,
  onboardingInputClass,
  onboardingInputErrorClass,
} from "@/components/onboarding/onboarding-shell"
import { cx } from "@/lib/utils/cx"
import { readWelcomeNavState } from "@/lib/welcome/nav-state"

/**
 * Step 1 of the personalised invitee welcome flow.
 *
 * Reachability: only valid when the user has just accepted an invite and a
 * one-shot bearer token is in localStorage (set by /invites/:uuid). The BE
 * setPassword endpoint is one-shot — 403s if hasSetPassword is already true
 * — so this page is impossible to land on twice for the same account.
 *
 * Team/role context comes from `location.state` (set by /invites/:uuid). On
 * a hard refresh the state is lost, and we fall back to generic copy and
 * the no-badge chrome — the form still works because the bearer token in
 * localStorage is what authenticates set-password.
 */
export default function WelcomePasswordPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { refreshUser } = useAuth()

  const navState = readWelcomeNavState(location.state)

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [showPwd, setShowPwd] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [profileEmail, setProfileEmail] = useState<string>("")

  // JWT-borne email for the "claiming this account" line, no extra round trip.
  useEffect(() => {
    try {
      const token = localStorage.getItem("etx_access_token")
      if (!token) return
      const payload = JSON.parse(atob(token.split(".")[1]))
      if (typeof payload?.email === "string") setProfileEmail(payload.email)
    } catch {
      // Token may be malformed; non-fatal — we just don't show the email line.
    }
  }, [])

  // Defensive: no token at all → user got here outside the flow.
  useEffect(() => {
    if (!localStorage.getItem("etx_access_token")) {
      navigate("/login", { replace: true })
    }
  }, [navigate])

  const validate = (): string | null => {
    if (password.length < 8 || password.length > 40)
      return "Password must be between 8 and 40 characters."
    if (password !== confirmPassword) return "Passwords don't match."
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    const v = validate()
    if (v) {
      setError(v)
      return
    }

    setIsSubmitting(true)
    try {
      const { accessToken } = await authApi.setPassword({ password })
      api.setToken(accessToken)
      await refreshUser()
      // Forward the same nav state so /welcome/profile gets the team/role badge.
      navigate("/welcome/profile", { replace: true, state: navState })
    } catch (err) {
      // 403 means hasSetPassword was already true — the user came back to
      // this page after already setting a password, or accessed it directly.
      if (err instanceof ApiError && err.status === 403) {
        api.clearToken()
        toast.message("This account already has a password — please sign in.")
        setTimeout(() => navigate("/login", { replace: true }), 800)
        return
      }
      setError(err instanceof Error ? err.message : "Couldn't set your password.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const team = navState
    ? { name: navState.teamName, role: navState.role }
    : undefined

  return (
    <WelcomeShell step="password" team={team}>
      <WelcomeCard
        eyebrow="Step 1 of 3"
        title={
          navState
            ? `Claim your seat at ${navState.teamName}.`
            : "Set your password."
        }
        subtitle="Choose a password to finish setting up your account. You can change it any time later."
        hero={<PasswordHero navState={navState} email={profileEmail} />}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {profileEmail && (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-center gap-3">
              <Mail01 className="size-4 text-zinc-400 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                  Account
                </p>
                <p className="text-[14px] text-zinc-900 font-medium truncate">{profileEmail}</p>
              </div>
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700"
            >
              {error}
            </motion.div>
          )}

          <Field label="Password" required>
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className={cx(onboardingInputClass, "pr-11", error && onboardingInputErrorClass)}
                required
                autoFocus
                autoComplete="new-password"
                minLength={8}
                maxLength={40}
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-zinc-400 hover:text-zinc-600 transition-colors"
                aria-label={showPwd ? "Hide password" : "Show password"}
              >
                {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </Field>

          <Field label="Confirm password" required>
            <div className="relative">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                className={cx(onboardingInputClass, "pr-11", error && onboardingInputErrorClass)}
                required
                autoComplete="new-password"
                minLength={8}
                maxLength={40}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute inset-y-0 right-3 flex items-center text-zinc-400 hover:text-zinc-600 transition-colors"
                aria-label={showConfirm ? "Hide password" : "Show password"}
              >
                {showConfirm ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </Field>

          <div className="pt-2">
            <PrimaryButton
              type="submit"
              loading={isSubmitting}
              disabled={password.length < 8 || password !== confirmPassword}
              size="wide"
              variant="dark"
            >
              Save password & continue
            </PrimaryButton>
          </div>
        </form>
      </WelcomeCard>
    </WelcomeShell>
  )
}

function PasswordHero({
  navState,
  email,
}: {
  navState: ReturnType<typeof readWelcomeNavState>
  email: string
}) {
  return (
    <div className="w-full max-w-sm space-y-5">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600">
          You're joining
        </p>
        <h3 className="mt-2 text-[26px] font-bold text-zinc-900 tracking-tight leading-tight">
          {navState?.teamName ?? "your team"}
        </h3>
        <p className="mt-1 text-[14px] text-zinc-500">
          as {navState?.role ?? "a teammate"}
        </p>
      </div>

      <div className="space-y-2.5">
        {[
          "Your account is already set up.",
          "Just pick a password and you're in.",
          email ? `Signed to ${email}.` : "Same email the invite was sent to.",
        ].map((line, i) => (
          <div key={i} className="flex items-start gap-2.5 text-[13px] text-zinc-600">
            <CheckCircle className="mt-0.5 size-4 text-emerald-500 shrink-0" aria-hidden />
            <span>{line}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
